import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid2 as Grid,
  Rating,
  Chip,
  LinearProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import { alpha, useTheme } from '@mui/material/styles';
import { bg, text as textColors, border, shadow, gradient } from '../../utils/darkModeAdapter';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUp';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDown';
import RecommendIcon from '@mui/icons-material/Recommend';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ScienceIcon from '@mui/icons-material/Science';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

// 評価結果を表示するコンポーネント
const EvaluationResult = ({ evaluationResult, applicantInfo, interviewerInfo, showTitle = true }) => {
  const theme = useTheme();
  if (!evaluationResult) return null;
  
  // データの安全性をチェック
  const safeEvaluationResult = {
    overall_score: evaluationResult.overall_score || 0,
    category_scores: evaluationResult.category_scores || {},
    strengths: evaluationResult.strengths || [],
    weaknesses: evaluationResult.weaknesses || [],
    company_match: evaluationResult.company_match || { percentage: 0, reason: '評価データが不完全です' },
    recommendation: evaluationResult.recommendation || '評価データが不完全です',
    // 音声解析統合用フィールド
    evaluation_mode: evaluationResult.evaluation_mode || 'checksheet',
    audio_analysis_included: evaluationResult.audio_analysis_included || false,
    score_breakdown: evaluationResult.score_breakdown || null,
    scientific_basis: evaluationResult.scientific_basis || null,
    // 科学的根拠に基づくチェックシート評価用フィールド
    evaluation_methodology: evaluationResult.evaluation_methodology || 'weighted_structured_interview',
    category_weights_applied: evaluationResult.category_weights_applied || {
      'スキル適合度': 0.40,
      'コミュニケーション力': 0.35,
      '人物評価': 0.25
    },
    scientific_references: evaluationResult.scientific_references || [
      'Sackett et al. (2022) - Structured Interview Validity: ρ=.42',
      'Wingate (2025) - Task Performance Validity: ρ=.30',
      'Wingate (2025) - Contextual Performance Validity: ρ=.28',
      'Smith & Kendall (1963) - BARS Methodology'
    ]
  };
  
  // 評価スコアの色を返す関数
  const getScoreColor = (score, max = 5) => {
    const percentage = score / max;
    if (percentage >= 0.8) return '#4caf50';
    if (percentage >= 0.6) return '#8bc34a';
    if (percentage >= 0.4) return '#ffc107';
    if (percentage >= 0.2) return '#ff9800';
    return '#f44336';
  };
  
  // マッチ度の色を返す関数
  const getMatchColor = (percentage) => {
    if (percentage >= 80) return '#4caf50';
    if (percentage >= 60) return '#8bc34a';
    if (percentage >= 40) return '#ffc107';
    if (percentage >= 20) return '#ff9800';
    return '#f44336';
  };

  // スコアに応じたアイコンを返す関数（アクセシビリティ強化）
  const getScoreIcon = (score, max = 100) => {
    const percentage = score / max;
    if (percentage >= 0.8) return <CheckCircleIcon sx={{ fontSize: 20, color: '#4caf50' }} />;
    if (percentage >= 0.6) return <WarningIcon sx={{ fontSize: 20, color: '#ff9800' }} />;
    return <ErrorIcon sx={{ fontSize: 20, color: '#f44336' }} />;
  };
  
  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        mb: 4,
        border: `1px solid ${border(theme).default}`,
        boxShadow: shadow(theme).card,
        background: gradient(theme).subtle
      }}
    >
      {showTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#249BE0' }}>
            面接評価結果
          </Typography>
        </Box>
      )}
      
      {/* 総合評価と企業マッチ度 - 強調表示 */}
      <Box 
        sx={{ 
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.05) 0%, rgba(33, 150, 243, 0.1) 100%)',
          border: '1px solid',
          borderColor: 'primary.light',
          boxShadow: '0 4px 12px rgba(63, 81, 181, 0.08)'
        }}
      >
        <Grid container spacing={4}>
          {/* 総合評価 - 大きく表示 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                {getScoreIcon(safeEvaluationResult.overall_score, 100)}
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  総合評価
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: getScoreColor(safeEvaluationResult.overall_score, 100)
                  }}
                >
                  {safeEvaluationResult.overall_score}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    position: 'absolute',
                    top: 30,
                    right: -50,
                    opacity: 0.8,
                    fontWeight: 500
                  }}
                >
                  /100
                </Typography>
              </Box>

              <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={safeEvaluationResult.overall_score}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: alpha('#000', 0.08),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getScoreColor(safeEvaluationResult.overall_score, 100),
                      borderRadius: 6
                    }
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>0</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>50</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>100</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* 企業マッチ度 - 大きく表示 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                企業マッチ度
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: getMatchColor(safeEvaluationResult.company_match.percentage)
                  }}
                >
                  {safeEvaluationResult.company_match.percentage}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    position: 'absolute',
                    top:30,
                    right: -30,
                    opacity: 0.8,
                    fontWeight: 500
                  }}
                >
                  %
                </Typography>
              </Box>

              <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={safeEvaluationResult.company_match.percentage}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: alpha('#000', 0.08),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getMatchColor(safeEvaluationResult.company_match.percentage),
                      borderRadius: 6
                    }
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>0%</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>50%</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>100%</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* マッチ度の理由 */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ textAlign: 'center', fontStyle: 'italic', color: 'text.secondary', mt: 1 }}>
              {safeEvaluationResult.company_match.reason}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* スコア内訳（音声解析が含まれる場合のみ表示） */}
      {safeEvaluationResult.score_breakdown && safeEvaluationResult.audio_analysis_included && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.05) 0%, rgba(103, 58, 183, 0.1) 100%)',
            border: '1px solid',
            borderColor: 'secondary.light',
            boxShadow: '0 4px 12px rgba(156, 39, 176, 0.08)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#9c27b0', 0.1),
                border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                mr: 1.5
              }}
            >
              <ScienceIcon sx={{ fontSize: 20, color: '#9c27b0' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0' }}>
              スコア内訳
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* 面接官評価スコア */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: `1px solid ${border(theme).default}`,
                  textAlign: 'center'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <AssessmentIcon sx={{ fontSize: 20, color: '#3f51b5', mr: 0.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    面接官評価
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#3f51b5' }}>
                  {safeEvaluationResult.score_breakdown.checksheet}
                </Typography>
                <Chip
                  label={`重み: ${(safeEvaluationResult.score_breakdown.checksheet_weight * 100).toFixed(0)}%`}
                  size="small"
                  sx={{
                    mt: 1,
                    bgcolor: alpha('#3f51b5', 0.1),
                    color: '#3f51b5',
                    fontWeight: 500
                  }}
                />
              </Box>
            </Grid>

            {/* 音声解析スコア */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: `1px solid ${border(theme).default}`,
                  textAlign: 'center'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <GraphicEqIcon sx={{ fontSize: 20, color: '#00bcd4', mr: 0.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    音声解析
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#00bcd4' }}>
                  {safeEvaluationResult.score_breakdown.audio || '-'}
                </Typography>
                <Chip
                  label={`重み: ${(safeEvaluationResult.score_breakdown.audio_weight * 100).toFixed(0)}%`}
                  size="small"
                  sx={{
                    mt: 1,
                    bgcolor: alpha('#00bcd4', 0.1),
                    color: '#00bcd4',
                    fontWeight: 500
                  }}
                />
              </Box>
            </Grid>

            {/* 統合スコア */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha('#9c27b0', 0.05),
                  border: '2px solid',
                  borderColor: '#9c27b0',
                  textAlign: 'center'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  統合スコア
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                  {safeEvaluationResult.score_breakdown.combined}
                </Typography>
                <Chip
                  label={safeEvaluationResult.score_breakdown.methodology === 'multimodal_integration' ? 'マルチモーダル統合' : '構造化面接のみ'}
                  size="small"
                  sx={{
                    mt: 1,
                    bgcolor: alpha('#9c27b0', 0.1),
                    color: '#9c27b0',
                    fontWeight: 500
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* 科学的根拠の注釈 */}
          {safeEvaluationResult.scientific_basis && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: bg(theme).subtle,
                border: `1px dashed ${border(theme).default}`
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                評価根拠
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Chip
                  label={`構造化面接の妥当性: ρ=${safeEvaluationResult.scientific_basis.structured_interview_validity}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
                <Chip
                  label={`音声解析の妥当性: ρ=${safeEvaluationResult.scientific_basis.audio_analysis_validity}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
                <Chip
                  label={`増分妥当性: Δρ=${safeEvaluationResult.scientific_basis.incremental_validity}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                出典: {safeEvaluationResult.scientific_basis.source}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Grid container spacing={3}>
        {/* カテゴリ別スコア */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>カテゴリ別スコア</Typography>
          <Grid container spacing={2}>
            {Object.entries(safeEvaluationResult.category_scores).map(([category, scoreData], index) => {
              // 新しい形式(オブジェクト)と古い形式(数値)の両方に対応
              const scoreValue = typeof scoreData === 'object' ? scoreData.score : scoreData;
              const details = typeof scoreData === 'object' ? scoreData.details : null;
              const strengths = typeof scoreData === 'object' ? scoreData.strengths : null;
              const improvements = typeof scoreData === 'object' ? scoreData.improvements : null;

              return (
                <Grid size={{ xs: 12, sm: 4 }} key={category}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1,
                      ease: [0.4, 0, 0.2, 1]
                    }}
                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    sx={{
                      p: 2.5,
                      bgcolor: 'background.paper',
                      borderRadius: 3,
                      boxShadow: shadow(theme).card,
                      border: `1px solid ${border(theme).default}`,
                      background: gradient(theme).card,
                      '&:hover': {
                        boxShadow: shadow(theme).lg
                      },
                      transition: 'box-shadow 0.3s ease'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, fontSize: '1rem' }}>{category}</Typography>
                    <Rating
                      value={scoreValue}
                      readOnly
                      precision={0.5}
                      max={5}
                      size="medium"
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: getScoreColor(scoreValue)
                        },
                        '& .MuiRating-iconEmpty': {
                          color: '#D1D5DB',
                          opacity: 1,
                        },
                      }}
                    />
                    <Typography variant="h6" sx={{ color: getScoreColor(scoreValue), fontWeight: 700, mt: 1 }}>
                      {scoreValue}/5
                    </Typography>

                    {details && (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${border(theme).default}` }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.6 }}>
                          {details}
                        </Typography>

                        {strengths && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#4caf50' }}>
                              強み:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                              {strengths}
                            </Typography>
                          </Box>
                        )}

                        {improvements && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#f44336' }}>
                              改善点:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                              {improvements}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        
        {/* 強み */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            whileHover={{ y: -8, transition: { duration: 0.3 } }}
            sx={{
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: shadow(theme).card,
              border: `1px solid ${border(theme).default}`,
              background: gradient(theme).card,
              '&:hover': {
                boxShadow: shadow(theme).lg
              },
              transition: 'box-shadow 0.3s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#4caf50', 0.1),
                  border: `1px solid ${alpha('#4caf50', 0.2)}`,
                  mr: 1.5
                }}
              >
                <ThumbUpAltIcon sx={{ fontSize: 20, color: '#4caf50' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>強み</Typography>
            </Box>
            <Box>
              {safeEvaluationResult.strengths.map((strength, index) => (
                <Chip
                  key={index}
                  label={strength}
                  sx={{
                    m: 0.5,
                    bgcolor: alpha('#4caf50', 0.1),
                    color: '#4caf50',
                    border: '1px solid',
                    borderColor: alpha('#4caf50', 0.3),
                    fontWeight: 500
                  }}
                />
              ))}
            </Box>
          </Box>
        </Grid>

        {/* 弱み */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            whileHover={{ y: -8, transition: { duration: 0.3 } }}
            sx={{
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: shadow(theme).card,
              border: `1px solid ${border(theme).default}`,
              background: gradient(theme).card,
              '&:hover': {
                boxShadow: shadow(theme).lg
              },
              transition: 'box-shadow 0.3s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#f44336', 0.1),
                  border: `1px solid ${alpha('#f44336', 0.2)}`,
                  mr: 1.5
                }}
              >
                <ThumbDownAltIcon sx={{ fontSize: 20, color: '#f44336' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>改善点</Typography>
            </Box>
            <Box>
              {safeEvaluationResult.weaknesses.map((weakness, index) => (
                <Chip
                  key={index}
                  label={weakness}
                  sx={{
                    m: 0.5,
                    bgcolor: alpha('#f44336', 0.1),
                    color: '#f44336',
                    border: '1px solid',
                    borderColor: alpha('#f44336', 0.3),
                    fontWeight: 500
                  }}
                />
              ))}
            </Box>
          </Box>
        </Grid>

        {/* 応募者情報 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
            whileHover={{ y: -8, transition: { duration: 0.3 } }}
            sx={{
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: shadow(theme).card,
              border: `1px solid ${border(theme).default}`,
              background: gradient(theme).card,
              '&:hover': {
                boxShadow: shadow(theme).lg
              },
              transition: 'box-shadow 0.3s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#2196f3', 0.1),
                  border: `1px solid ${alpha('#2196f3', 0.2)}`,
                  mr: 1.5
                }}
              >
                <PersonIcon sx={{ fontSize: 20, color: '#2196f3' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>応募者情報</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 110 }}>氏名:</Typography>
                <Typography variant="body2">{applicantInfo.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 110 }}>フリガナ:</Typography>
                <Typography variant="body2">{applicantInfo.nameKana}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 110 }}>希望の役職:</Typography>
                <Typography variant="body2">{applicantInfo.desiredPosition}</Typography>
              </Box>
              {applicantInfo.notes && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 110 }}>特記事項:</Typography>
                  <Typography variant="body2">{applicantInfo.notes}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

        {/* 面接者情報 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            whileHover={{ y: -8, transition: { duration: 0.3 } }}
            sx={{
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: shadow(theme).card,
              border: `1px solid ${border(theme).default}`,
              background: gradient(theme).card,
              '&:hover': {
                boxShadow: shadow(theme).lg
              },
              transition: 'box-shadow 0.3s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#9c27b0', 0.1),
                  border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                  mr: 1.5
                }}
              >
                <SupervisorAccountIcon sx={{ fontSize: 20, color: '#9c27b0' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>面接者情報</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 110 }}>氏名:</Typography>
                <Typography variant="body2">{interviewerInfo.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 110 }}>所属部署:</Typography>
                <Typography variant="body2">{interviewerInfo.department}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 110 }}>役職:</Typography>
                <Typography variant="body2">{interviewerInfo.position}</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* 採用担当者へのアドバイス */}
        <Grid size={{ xs: 12 }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            whileHover={{ y: -8, transition: { duration: 0.3 } }}
            sx={{
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: shadow(theme).card,
              border: `1px solid ${border(theme).default}`,
              background: gradient(theme).card,
              '&:hover': {
                boxShadow: shadow(theme).lg
              },
              transition: 'box-shadow 0.3s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#3f51b5', 0.1),
                  border: `1px solid ${alpha('#3f51b5', 0.2)}`,
                  mr: 1.5
                }}
              >
                <RecommendIcon sx={{ fontSize: 20, color: '#3f51b5' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>採用担当者へのアドバイス</Typography>
            </Box>
            <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.7 }}>
              {safeEvaluationResult.recommendation}
            </Typography>
          </Box>
        </Grid>

        {/* 評価方法論 */}
        <Grid size={{ xs: 12 }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.9 }}
            sx={{
              p: 2.5,
              bgcolor: bg(theme).subtle,
              borderRadius: 3,
              border: `1px dashed ${border(theme).default}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#607d8b', 0.1),
                  border: `1px solid ${alpha('#607d8b', 0.2)}`,
                  mr: 1.5
                }}
              >
                <ScienceIcon sx={{ fontSize: 18, color: '#607d8b' }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#607d8b' }}>
                評価方法論
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary" paragraph sx={{ mb: 2 }}>
              本評価は産業組織心理学のメタ分析研究に基づく重み付け構造化面接手法（Weighted Structured Interview）を採用しています。
              BARS（行動アンカー評価尺度）に基づき、客観的かつ再現性の高い評価を実現しています。
            </Typography>

            {/* カテゴリ重み付け表示 */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
                カテゴリ重み付け（研究に基づく配分）
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(safeEvaluationResult.category_weights_applied).map(([category, weight]) => (
                  <Chip
                    key={category}
                    label={`${category}: ${(weight * 100).toFixed(0)}%`}
                    size="small"
                    sx={{
                      bgcolor: alpha('#607d8b', 0.1),
                      color: '#607d8b',
                      fontSize: '0.7rem',
                      height: 24
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* 科学的根拠の出典 */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
                研究出典
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {safeEvaluationResult.scientific_references.map((ref, index) => (
                  <Typography key={index} variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.65rem' }}>
                    • {ref}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EvaluationResult; 